import { removeCookie, setCookieV2, getCookie } from '../../SharedLib/Util/CookieUtil';
import type { IUserSessionLifespan } from '../../SharedLib/Util/Interfaces';
import type { ErrorType, IOperationResult } from '../../SharedLib/Util/InterfaceAndTypeUtil';
import { logging } from '../../SharedLib/Util/LoggingUtil';
import { getPathfinderUrl, getProfileUrl } from '../../SharedLib/Util/EnvironmentUtil';
import { getApiBaseDomain } from '../../SharedLib/Util/InternalProjects/BaseApiUtil';
import { getLocalCurrentUts } from '../../SharedLib/Util/TimeUtil';
import {
  cacheRedirectUrl,
  getSanitizedRedirectUrl,
  safeRedirect,
  tweakRedirectUrlOnSuccessfulSignup,
} from '../../SharedLib/Util/RedirectUtil';
import { authUtil } from '../../SharedLib/Util/AuthUtil';
import { fetchProfile } from '../../SharedLib/Util/InternalProjects/ProfileUtil';

/**
 * When `false` (default), social login buttons use the custom IdP route:
 * `/social-auth?init=true&…` → `auth…/oauth2/authorize`. When `true`, Cognito Hosted UI
 * ({@link authUtil.federatedLogin}) is used instead.
 */
export const LEGACY_AUTH_ENABLED = false;

/** Lowercase IDs for `/oauth2/authorize` and `/social-auth?init=true&provider=`. */
export const CUSTOM_SOCIAL_INIT_PROVIDERS = ['google', 'facebook', 'signinwithapple', 'github', 'classlink', 'feide'] as const;

export type CustomSocialAuthProvider = (typeof CUSTOM_SOCIAL_INIT_PROVIDERS)[number];

/**
 * Cross-route progress for custom social login (`{@link CUSTOM_SOCIAL_AUTH_STATE_STORAGE_KEY}`), for hosts that
 * cannot rely on SPA memory across redirects / future iframe or popup parents.
 *
 * - **Cookie:** `.w3schools.com`, **1 hour** TTL (refreshed on each write via `max-age`).
 * - **Encoded value:** URI-encoded JSON of {@link CustomSocialAuthStateCookie} (`IOperationResult` + `data` payload).
 * - **Logout / new init:** cookie is cleared in profile **Component** code (see `Logout.tsx`, signup flows, Settings)
 *   and, in **`?popup=true` flows**, recreated on each `?init=true&provider=` bootstrap before the IdP redirect.
 *
 * **`handleError`:** in **popup** mode, persists error to the progress cookie then `window.close()`. Otherwise
 * redirects to `/login` with query params (no progress cookie writes).
 */
export const CUSTOM_SOCIAL_AUTH_STATE_COOKIE_MAX_AGE_SEC = 3600;

/** One progress marker; {@link CustomSocialAuthStatePayload.currentStep} and `history[].step` use `UPPER_SNAKE_CASE`. */
export type CustomSocialAuthStateHistoryEntry = {
  step: string;
  createdUts: number;
  description: string;
};

export type CustomSocialAuthStatePayload = {
  history: CustomSocialAuthStateHistoryEntry[];
  currentStep: string;
  expiryUts: number;
  completed: boolean;
  percentage: number;
  provider?: CustomSocialAuthProvider;
};

/** Mirrors session consume {@link IOperationResult}; terminal failures set non-`0` {@link CustomSocialAuthStateCookie.error}. */
export type CustomSocialAuthStateCookie = IOperationResult<CustomSocialAuthStatePayload>;

export const CUSTOM_SOCIAL_AUTH_STATE_STORAGE_KEY = 'customSocialAuthState';

const OP_OK: ErrorType = { code: '0', description: 'OK' };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const isCustomSocialAuthStateCookie = (v: unknown): v is CustomSocialAuthStateCookie => {
  if (!isRecord(v)) {
    return false;
  }

  const err = v.error;
  const data = v.data;
  if (!isRecord(err) || typeof err.code !== 'string' || !isRecord(data)) {
    return false;
  }

  if (
    typeof data.currentStep !== 'string'
    || typeof data.expiryUts !== 'number'
    || typeof data.completed !== 'boolean'
    || typeof data.percentage !== 'number'
    || !Array.isArray(data.history)
  ) {
    return false;
  }

  const okHistory = data.history.every((h): h is CustomSocialAuthStateHistoryEntry =>
    isRecord(h)
      && typeof h.step === 'string'
      && typeof h.createdUts === 'number'
      && typeof h.description === 'string');

  return okHistory;
};

/** sessionStorage key for OAuth `state` issued on authorize; echoed in `/social-auth` URL for correlation. */
export const CUSTOM_SOCIAL_AUTH_OAUTH_STATE_STORAGE_KEY = 'customSocialAuthOauthState';

/** sessionStorage key for PKCE `code_verifier`; sent as `codeVerifier` on `/api/user/session` consume when required. */
export const CUSTOM_SOCIAL_AUTH_OAUTH_CODE_VERIFIER_STORAGE_KEY = 'customSocialAuthOauthCodeVerifier';

/**
 * Session flag: `?popup=true` on `/social-auth` cannot always round-trip through the IdP redirect URI
 * (callback URL is registered without query params), so once the user hits `…&popup=true` we persist that
 * for the rest of this tab’s OAuth flow.
 */
export const CUSTOM_SOCIAL_AUTH_POPUP_MODE_SESSION_KEY = 'customSocialAuthPopupMode';

/**
 * Keep {@link CUSTOM_SOCIAL_AUTH_POPUP_MODE_SESSION_KEY} aligned with the current URL (init bootstrap clears
 * the flag unless `popup=true`). Returns whether this tab should use the popup flow (progress cookie + `close()`).
 */
export const syncCustomSocialAuthPopupModeFromQuery = (queryParams: URLSearchParams): boolean => {
  try {
    if (queryParams.get('popup') === 'true') {
      sessionStorage.setItem(CUSTOM_SOCIAL_AUTH_POPUP_MODE_SESSION_KEY, 'true');
    }

    const isBootstrapInit =
      queryParams.get('init') === 'true'
      && !queryParams.get('consume_token')
      && !queryParams.get('error');

    if (isBootstrapInit && queryParams.get('popup') !== 'true') {
      sessionStorage.removeItem(CUSTOM_SOCIAL_AUTH_POPUP_MODE_SESSION_KEY);
    }

    return sessionStorage.getItem(CUSTOM_SOCIAL_AUTH_POPUP_MODE_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

export const isCustomSocialAuthPopupMode = (): boolean => {
  try {
    return sessionStorage.getItem(CUSTOM_SOCIAL_AUTH_POPUP_MODE_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

/** True when custom IdP is active ({@link LEGACY_AUTH_ENABLED} is `false`). */
export const isCustomSocialAuthEnabled = (): boolean => !LEGACY_AUTH_ENABLED;

/** Matches `UserSessionStatusCodeEnum.Active` in profile UI SharedLib. */
const USER_SESSION_STATUS_ACTIVE = '1';

const USER_SESSION_STATUS_COOKIE_TTL_SEC = 7776000; // 90 days

export type UpsertConsumeSessionCookiesResult = {
  error: { code: string; description?: string };
};

const createRandomBase64Url = (bytes = 32): string => {
  const arr = new Uint8Array(bytes);
  window.crypto.getRandomValues(arr);
  let bin = '';
  arr.forEach(b => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const sha256Base64Url = (input: string): Promise<string> => {
  const enc = new TextEncoder();
  return window.crypto.subtle.digest('SHA-256', enc.encode(input)).then(buf => {
    const bytes = new Uint8Array(buf);
    let bin = '';
    bytes.forEach(b => {
      bin += String.fromCharCode(b);
    });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  });
};

export const getStoredCustomSocialState = (): string | null => {
  try {
    return sessionStorage.getItem(CUSTOM_SOCIAL_AUTH_OAUTH_STATE_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const getStoredCustomSocialVerifier = (): string | undefined => {
  try {
    const v = sessionStorage.getItem(CUSTOM_SOCIAL_AUTH_OAUTH_CODE_VERIFIER_STORAGE_KEY);
    return v || undefined;
  } catch {
    return undefined;
  }
};

const decodeCustomSocialAuthStateCookiePayload = (raw: string): CustomSocialAuthStateCookie | null => {
  try {
    const decoded = decodeURIComponent(raw);
    const parsed: unknown = JSON.parse(decoded);
    return isCustomSocialAuthStateCookie(parsed) ? parsed : null;
  } catch {
    try {
      const parsed: unknown = JSON.parse(raw);
      return isCustomSocialAuthStateCookie(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
};

/** Read and validate {@link CUSTOM_SOCIAL_AUTH_STATE_STORAGE_KEY} from cookies (embedded JSON, URI-encoded). */
export const readCustomSocialAuthStateFromCookie = (): CustomSocialAuthStateCookie | null => {
  try {
    const raw = getCookie(CUSTOM_SOCIAL_AUTH_STATE_STORAGE_KEY);
    if (!raw || raw.trim() === '') {
      return null;
    }
    return decodeCustomSocialAuthStateCookiePayload(raw);
  } catch {
    return null;
  }
};

export const removeCustomSocialAuthStateCookie = (): void => {
  removeCookie(CUSTOM_SOCIAL_AUTH_STATE_STORAGE_KEY);
};

const writeCustomSocialAuthStateCookie = (state: CustomSocialAuthStateCookie): void => {
  const now = getLocalCurrentUts();
  const merged: CustomSocialAuthStateCookie = {
    ...state,
    data: {
      ...state.data,
      expiryUts: now + CUSTOM_SOCIAL_AUTH_STATE_COOKIE_MAX_AGE_SEC,
    },
  };
  setCookieV2({
    name: CUSTOM_SOCIAL_AUTH_STATE_STORAGE_KEY,
    value: encodeURIComponent(JSON.stringify(merged)),
    maxAge: CUSTOM_SOCIAL_AUTH_STATE_COOKIE_MAX_AGE_SEC,
  });
};

/** Clear prior cookie then seed a new trace for `/social-auth?init=true`. */
export const resetCustomSocialAuthStateCookieForNewInit = (provider: CustomSocialAuthProvider): void => {
  removeCustomSocialAuthStateCookie();
  const now = getLocalCurrentUts();
  const ttl = CUSTOM_SOCIAL_AUTH_STATE_COOKIE_MAX_AGE_SEC;
  writeCustomSocialAuthStateCookie({
    error: OP_OK,
    data: {
      history: [
        {
          step: 'INIT',
          createdUts: now,
          description: `Custom social auth started (${provider})`,
        },
      ],
      currentStep: 'INIT',
      expiryUts: now + ttl,
      completed: false,
      percentage: 5,
      provider,
    },
  });
};

/** Upsert cookie: append a step and optionally `percentage` / `completed`; no-op if no cookie. */
export const advanceCustomSocialAuthStateCookie = ({
  step,
  description,
  percentage,
  completed,
}: {
  step: string;
  description: string;
  percentage?: number;
  completed?: boolean;
}): void => {
  try {
    const prev = readCustomSocialAuthStateFromCookie();
    if (!prev) {
      return;
    }
    const now = getLocalCurrentUts();
    const nextPct = percentage ?? prev.data.percentage;
    const clamped = Math.min(100, Math.max(0, nextPct));
    writeCustomSocialAuthStateCookie({
      error: OP_OK,
      data: {
        ...prev.data,
        history: [
          ...prev.data.history,
          { step, createdUts: now, description },
        ],
        currentStep: step,
        completed: completed ?? prev.data.completed,
        percentage: clamped,
        expiryUts: now + CUSTOM_SOCIAL_AUTH_STATE_COOKIE_MAX_AGE_SEC,
      },
    });
  } catch (e) {
    logging.logError('Custom social auth state cookie: advance failed', e);
  }
};

const persistCustomSocialAuthStateCookieBeforeLoginRedirect = (error: ErrorType): void => {
  try {
    const prev = readCustomSocialAuthStateFromCookie();
    const now = getLocalCurrentUts();
    const ttl = CUSTOM_SOCIAL_AUTH_STATE_COOKIE_MAX_AGE_SEC;
    const descRaw =
      error.description != null && String(error.description).trim() !== ''
        ? String(error.description).trim()
        : error.code;
    const baseHistory = prev?.data.history ?? [];
    writeCustomSocialAuthStateCookie({
      error,
      data: {
        history: [
          ...baseHistory,
          { step: 'ERROR', createdUts: now, description: descRaw },
        ],
        currentStep: 'ERROR',
        expiryUts: now + ttl,
        completed: false,
        percentage: prev?.data.percentage ?? 0,
        provider: prev?.data.provider,
      },
    });
  } catch (e) {
    logging.logError('Custom social auth state cookie: failed to persist error', e);
  }
};

const clearCustomSocialPkceSessionKeys = (): void => {
  sessionStorage.removeItem(CUSTOM_SOCIAL_AUTH_OAUTH_STATE_STORAGE_KEY);
  sessionStorage.removeItem(CUSTOM_SOCIAL_AUTH_OAUTH_CODE_VERIFIER_STORAGE_KEY);
};

/**
 * Clears PKCE session keys. **Popup:** persist error on the progress cookie and `window.close()`.
 * **Root:** redirect to `/login` with `error_code` / `error_description` (no progress cookie).
 */
export const handleError = (error: ErrorType): void => {
  const popup = isCustomSocialAuthPopupMode();
  if (popup) {
    persistCustomSocialAuthStateCookieBeforeLoginRedirect(error);
  }
  clearCustomSocialPkceSessionKeys();
  if (popup) {
    window.close();
    return;
  }
  const description =
    error.description != null && String(error.description).trim() !== ''
      ? String(error.description).trim()
      : 'Something went wrong. Please try again.';
  const qp = new URLSearchParams({
    error_code: error.code,
    error_description: description,
  });
  window.location.replace(`/login?${qp.toString()}`);
};

export const parseCustomSocialAuthInitProvider = (providerRaw: string | null): CustomSocialAuthProvider | null => {
  if (!providerRaw) {
    return null;
  }
  const p = providerRaw.trim().toLowerCase();
  return (CUSTOM_SOCIAL_INIT_PROVIDERS as readonly string[]).includes(p) ? (p as CustomSocialAuthProvider) : null;
};

export const startCustomSocialAuthorizeRedirect = (provider: CustomSocialAuthProvider): Promise<void> => {
  const state = createRandomBase64Url(16);
  const codeVerifier = createRandomBase64Url(32);
  return sha256Base64Url(codeVerifier).then(codeChallenge => {
    try {
      sessionStorage.setItem(CUSTOM_SOCIAL_AUTH_OAUTH_STATE_STORAGE_KEY, state);
      sessionStorage.setItem(CUSTOM_SOCIAL_AUTH_OAUTH_CODE_VERIFIER_STORAGE_KEY, codeVerifier);
    } catch {
      logging.logError('Social auth init: sessionStorage unavailable');
      handleError({
        code: 'SESSUNSUI',
        description: 'Could not start sign-in from this browser. Please try again.',
      });
      return;
    }

    if (isCustomSocialAuthPopupMode()) {
      advanceCustomSocialAuthStateCookie({
        step: 'AUTHORIZE_REDIRECT',
        description: 'Redirecting to identity provider',
        percentage: 25,
      });
    }

    const redirectUri = `${getProfileUrl()}/social-auth`;
    const baseAuthUrl = `https://auth.${getApiBaseDomain()}/oauth2/authorize`;
    const params = new URLSearchParams({
      provider,
      redirect_uri: redirectUri,
      state,
      code_challenge: codeChallenge,
    });
    window.location.replace(`${baseAuthUrl}?${params.toString()}`);
  });
};

/**
 * Sets readable session cookies after a successful `/api/user/session` consume.
 * Use when the build does not ship full `UserSessionUtil.upsertActiveUserSessionCookies`,
 * or to keep one implementation shared with the `/social-auth` callback.
 */
export const upsertSessionCookiesAfterConsume = ({
  accessToken,
  userInfoCookie,
  sessionId,
  sessionLifespan,
}: {
  accessToken: string;
  userInfoCookie: string;
  sessionId: string;
  sessionLifespan: IUserSessionLifespan;
}): UpsertConsumeSessionCookiesResult => {
  const currentUts = getLocalCurrentUts();

  const expiryUts =
    sessionLifespan.accessTokenExpiryUiUts > sessionLifespan.refreshTokenExpiryUiUts
      ? sessionLifespan.accessTokenExpiryUiUts
      : sessionLifespan.refreshTokenExpiryUiUts;

  const accessTokenTtl = expiryUts - currentUts;
  if (accessTokenTtl <= 0) {
    return {
      error: { code: 'IARCT', description: 'Invalid access / refresh cookie TTL' },
    };
  }

  const accessTokenExpiryDto = new Date(expiryUts * 1000);

  const userSessionCookieExpiryUts = sessionLifespan.currentUiUts + USER_SESSION_STATUS_COOKIE_TTL_SEC;
  const userSessionCookieExpiryDto = new Date(userSessionCookieExpiryUts * 1000);

  const userSessionMeta = {
    id: sessionId,
    iss: sessionLifespan.refreshTokenBaseUiUts,
    atexp: sessionLifespan.accessTokenExpiryUiUts,
    rtexp: sessionLifespan.refreshTokenExpiryUiUts,
  };

  let userSessionMetaB64: string;
  try {
    userSessionMetaB64 = btoa(JSON.stringify(userSessionMeta));
  } catch {
    return { error: { code: 'FEUSM', description: 'Failed encoding user session meta' } };
  }

  const replaceCookie = (prs: Parameters<typeof setCookieV2>[0]) => {
    removeCookie(prs.name);
    setCookieV2(prs);
  };

  replaceCookie({
    name: 'userSession',
    value: USER_SESSION_STATUS_ACTIVE,
    maxAge: USER_SESSION_STATUS_COOKIE_TTL_SEC,
    expires: userSessionCookieExpiryDto,
  });

  replaceCookie({
    name: 'userSessionMeta',
    value: userSessionMetaB64,
    maxAge: USER_SESSION_STATUS_COOKIE_TTL_SEC,
    expires: userSessionCookieExpiryDto,
  });

  replaceCookie({
    name: 'accessToken',
    value: accessToken,
    maxAge: accessTokenTtl,
    expires: accessTokenExpiryDto,
  });

  replaceCookie({
    name: '__c_u_i_1',
    value: userInfoCookie,
    maxAge: accessTokenTtl,
    expires: accessTokenExpiryDto,
  });

  return { error: { code: '0' } };
};

/** Cache `redirect_url` from the current `/social-auth` URL (same as login pages). */
export const cacheCustomSocialAuthRedirectUrl = (): void => {
  cacheRedirectUrl();
};

/**
 * Full-page success: redirect using cached / query `redirect_url`, aligned with
 * {@link ../NewLogin/LoginPanel/LoginPanel} social flow and {@link ../../utils/handle-logged-in}.
 */
export const finishCustomSocialAuthFullPageRedirect = async ({
  isNewUser,
}: {
  isNewUser: boolean;
}): Promise<void> => {
  if (isNewUser) {
    tweakRedirectUrlOnSuccessfulSignup();
  }

  try {
    const user = await authUtil.getUserInfoViaAccessTokenCookie();
    const attributes = Object.fromEntries(
      ((user?.data?.attributes || []) as Record<string, string>[]).map(({ Name, Value }) => [
        Name,
        Value,
      ]),
    );

    if (attributes['custom:isFirstLogin'] === 'true') {
      await fetchProfile({
        method: 'PATCH',
        url: '/user/unset-is-first-login',
      });
    }

    const redirectUrlBeforeFeideCheck = getSanitizedRedirectUrl();
    if (attributes['custom:showFeideWelcomeMsg'] === 'true' && !redirectUrlBeforeFeideCheck) {
      safeRedirect(`${getPathfinderUrl()}?showFeideWelcomeMsg=true`);
      return;
    }
  } catch (err) {
    logging.logError('Custom social auth: post-login redirect prep failed', err);
  }

  const redirectUrl = getSanitizedRedirectUrl({ fallback: getPathfinderUrl() });
  safeRedirect(redirectUrl);
};
